import { useState } from 'react'
import { ESTADOS_COMERCIALES, type Rec } from '../../shared/schema'
import { quickUpdate, updateRecord, useRecords } from '../lib/api'
import { useSession } from '../lib/auth'
import { useEditor } from '../lib/useOpen'
import { Link, useLocation } from '../lib/router'
import { fmtDay, fmtTime, today, usd, waNumber } from '../lib/format'
import { Kanban } from '../components/Boards'
import { FileLink, RecordForm } from '../components/RecordForm'
import { Avatar, Badge, Button, Empty, ErrorNote, Icon, PageHeader, Segmented, Skeleton, toneOf, useToast } from '../components/ui'

type Seg = 'todos' | 'comprador' | 'inversor'

export function Clientes() {
  const { query } = useLocation()
  const { data, loading, error, refresh } = useRecords('clientes')
  const props = useRecords('propiedades').data
  const visitas = useRecords('visitas').data
  const ed = useEditor(data)
  const toast = useToast()
  const [seg, setSeg] = useState<Seg>('todos')
  const [view, setView] = useState<'lista' | 'kanban'>('lista')
  const [q, setQ] = useState('')
  const [soloPend, setSoloPend] = useState(query.get('cumplimiento') === 'pendiente')

  const list = data.filter(c =>
    (seg === 'todos' || (seg === 'comprador' ? c.segmento === 'Comprador de vivienda' : c.segmento === 'Inversor del exterior')) &&
    (!q || [c.nombre, c.pais, c.email, c.telefono, c.ref].join(' ').toLowerCase().includes(q.toLowerCase())) &&
    (!soloPend || c.cumplimiento !== 'Aprobado'))

  const defaults = seg === 'inversor' ? { segmento: 'Inversor del exterior', socia: 'Yesica', estado: 'Nuevo', cumplimiento: 'Pendiente' }
    : seg === 'comprador' ? { segmento: 'Comprador de vivienda', socia: 'Leticia', estado: 'Nuevo', cumplimiento: 'Pendiente' }
    : { estado: 'Nuevo', cumplimiento: 'Pendiente' }

  return (
    <div className="page">
      <PageHeader eyebrow="Módulo 2" title="Clientes e inversores" subtitle="Compradores de vivienda con Leticia, inversores del exterior con Yesica."
        actions={<Button variant="primary" icon="plus" onClick={() => ed.create(defaults)}>Registrar cliente</Button>} />

      <div className="toolbar">
        <Segmented value={seg} onChange={setSeg} options={[
          { value: 'todos', label: `Todos · ${data.length}` },
          { value: 'comprador', label: 'Compradores · Leticia' },
          { value: 'inversor', label: 'Inversores · Yesica' },
        ]} />
        <div className="toolbar-right">
          <div className="search-input"><Icon name="search" size={16} /><input id="cli-q" type="search" placeholder="Buscar cliente" value={q} onChange={e => setQ(e.target.value)} aria-label="Buscar clientes" /></div>
          <button type="button" className={`chip ${soloPend ? 'on' : ''}`} onClick={() => setSoloPend(!soloPend)}><span className="comp-dot red" /> Documentación pendiente</button>
          <Segmented value={view} onChange={setView} options={[{ value: 'lista', label: 'Lista', icon: 'list' }, { value: 'kanban', label: 'Kanban', icon: 'kanban' }]} />
        </div>
      </div>

      {error && <ErrorNote error={error} onRetry={refresh} />}
      {loading ? <Skeleton rows={5} /> : !list.length ? (
        <Empty title={data.length ? 'Nadie coincide con el filtro' : 'Todavía no hay clientes'} text={data.length ? undefined : 'Registrá la primera consulta para empezar el seguimiento.'} />
      ) : view === 'lista' ? (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Cliente</th><th>Tipo</th><th>Estado</th><th className="num">Presupuesto</th><th>Detalle</th><th>Contacto</th><th>Socia</th></tr></thead>
            <tbody>
              {list.map(c => (
                <tr key={c.id} onClick={() => ed.open(c)} tabIndex={0} onKeyDown={e => e.key === 'Enter' && ed.open(c)}>
                  <td><div className="cell-name"><Compliance c={c} /><div><strong>{c.nombre}</strong><small>{c.ref}</small></div></div></td>
                  <td>{c.segmento === 'Inversor del exterior' ? 'Inversor' : c.segmento ? 'Comprador' : '—'}</td>
                  <td><Badge>{c.estado}</Badge></td>
                  <td className="num">{usd(c.presupuesto)}</td>
                  <td className="cell-muted">{c.segmento === 'Inversor del exterior' ? [c.pais, c.rentaEsperada && `${c.rentaEsperada} % esperado`].filter(Boolean).join(' · ') : (c.preferencias ?? '').slice(0, 48)}</td>
                  <td onClick={e => e.stopPropagation()}>{(c.whatsapp || c.telefono) && <a className="wa" href={`https://wa.me/${waNumber(c.whatsapp || c.telefono)}`} target="_blank" rel="noreferrer"><Icon name="chat" size={14} /> {c.whatsapp || c.telefono}</a>}</td>
                  <td><Avatar name={c.socia} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Kanban columns={ESTADOS_COMERCIALES.map(e => ({ key: e, tone: toneOf(e) }))} items={list} groupBy={c => c.estado}
          onCardClick={ed.open}
          onMove={async (c, estado) => { try { await quickUpdate('clientes', c, { estado }); toast(`${c.nombre} → ${estado}`) } catch (e: any) { toast(e.message, 'error') } }}
          render={c => (
            <>
              <div className="kc-top"><Compliance c={c} /><strong>{c.nombre}</strong></div>
              <div className="kc-meta">{c.segmento === 'Inversor del exterior' ? `Inversor · ${c.pais || 'exterior'}` : 'Comprador'}</div>
              <div className="kc-foot"><span>{usd(c.presupuesto)}</span><Avatar name={c.socia} /></div>
            </>
          )} />
      )}

      <RecordForm col="clientes" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        top={() => ed.editing && <ClienteContext c={ed.editing} props={props} visitas={visitas} onChanged={ed.open} />} />
    </div>
  )
}

export const Compliance = ({ c }: { c: Rec }) => {
  const ok = c.cumplimiento === 'Aprobado'
  return <span className={`comp-dot ${ok ? 'green' : 'red'}`} title={ok ? `Documentación aprobada por ${c.revisadoPor ?? '—'}` : 'Documentación pendiente'} aria-label={ok ? 'Documentación aprobada' : 'Documentación pendiente'} />
}

function ClienteContext({ c, props, visitas, onChanged }: { c: Rec; props: Rec[]; visitas: Rec[]; onChanged: (r: Rec) => void }) {
  const { name } = useSession()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [armed, setArmed] = useState(false)
  const ok = c.cumplimiento === 'Aprobado'
  const vis = visitas.filter(v => v.cliente === c.id).sort((a, b) => b.inicio.localeCompare(a.inicio))
  const interes = props.filter(p => (c.propiedadesInteres ?? []).includes(p.id))
  const docs: string[] = c.docsDD ?? []

  async function setCompliance(approve: boolean) {
    setBusy(true)
    try {
      const saved = await updateRecord('clientes', c, approve
        ? { cumplimiento: 'Aprobado', revisadoPor: name, fechaAprobacion: today() }
        : { cumplimiento: 'Pendiente', revisadoPor: '', fechaAprobacion: '' })
      toast(approve ? 'Documentación aprobada y registrada en Notion' : 'Documentación marcada como pendiente')
      onChanged(saved)
    } catch (e: any) { toast(e.message, 'error') } finally { setBusy(false); setArmed(false) }
  }

  return (
    <div className="context">
      <div className={`compliance-box ${ok ? 'ok' : 'pending'}`}>
        <span className={`comp-dot big ${ok ? 'green' : 'red'}`} />
        <div className="grow">
          <strong>{ok ? 'Documentación revisada y aprobada' : 'Documentación de debida diligencia pendiente'}</strong>
          <p>{ok ? `Aprobó ${c.revisadoPor} el ${fmtDay(c.fechaAprobacion)}.` : docs.length ? `${docs.length} documento${docs.length > 1 ? 's' : ''} cargado${docs.length > 1 ? 's' : ''}. Falta la revisión de una socia.` : 'Todavía no hay documentos cargados.'}</p>
          {docs.length > 0 && <div className="col gap-xs">{docs.map((d, i) => <FileLink key={i} value={d} />)}</div>}
        </div>
        {ok ? <Button size="sm" variant="quiet" busy={busy} onClick={() => setCompliance(false)}>Volver a pendiente</Button>
          : armed ? <Button size="sm" variant="accent" busy={busy} icon="check" onClick={() => setCompliance(true)}>Confirmo que revisé ({name})</Button>
          : <Button size="sm" variant="ghost" icon="shield" onClick={() => setArmed(true)}>Aprobar revisión</Button>}
      </div>
      <p className="hint">La aprobación la hace una socia a mano y queda registrada con su nombre y la fecha. Cargá los documentos en la sección Cumplimiento y guardá antes de aprobar.</p>
      {(c.whatsapp || c.telefono) && <a className="inline-link" href={`https://wa.me/${waNumber(c.whatsapp || c.telefono)}`} target="_blank" rel="noreferrer"><Icon name="chat" size={14} /> Escribir por WhatsApp</a>}
      {interes.length > 0 && <div className="ctx-block"><span className="ctx-label">Propiedades de interés</span><div className="chips">{interes.map(p => <Link key={p.id} to={`/propiedades?abrir=${p.id}`} className="chip">{p.nombre}</Link>)}</div></div>}
      <div className="ctx-block">
        <span className="ctx-label">Historial de visitas</span>
        {vis.length ? <ul className="ctx-list">{vis.map(v => <li key={v.id}><span>{fmtDay(v.inicio.slice(0, 10))} · {fmtTime(v.inicio)}</span> {v.titulo} <Badge>{v.estado}</Badge></li>)}</ul> : <p className="muted">Sin visitas todavía.</p>}
        <Link to={`/agenda?nuevo=1&cliente=${c.id}`} className="inline-link"><Icon name="plus" size={14} /> Programar visita</Link>
      </div>
    </div>
  )
}
